import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Loader from 'components/loader'
import Dialoguemodal from 'components/dialoguemodal'
import ConfirmationModal from 'components/confimation-modal'
import SearchList from 'components/search-list'
import { currencyFormat } from 'utils'
import { productsApi } from '../restaurant.api'
import { ProductsProvider, useProducts } from './products.context'
import ProductForm from './product-form'

function StatusChip({ label, tone }) {
  const tones = {
    warning: 'tw-bg-[#FBF1DE] tw-text-[#7F5F1E]',
    danger: 'tw-bg-[#FBE9E7] tw-text-[#C0392B]',
    success: 'tw-bg-[#E6F0E9] tw-text-[#3F7A56]',
  }
  return (
    <span
      className={`tw-text-[10px] tw-px-[7px] tw-py-[2px] tw-rounded-[3px] tw-whitespace-nowrap ${tones[tone] || tones.success}`}
    >
      {label}
    </span>
  )
}

function ActionButton({ label, onClick, tone }) {
  const tones = {
    danger: 'tw-text-[#C0392B] tw-border-[#FBE9E7]',
    default: 'tw-text-gray-600 tw-border-gray-300',
  }
  return (
    <button
      type='button'
      onClick={onClick}
      className={`tw-text-[12px] tw-px-[10px] tw-py-[4px] tw-rounded-[5px] tw-border ${tones[tone] || tones.default}`}
    >
      {label}
    </button>
  )
}

function ProductCard({ product, onEdit, onChanged, onDelete }) {
  const onSale = product.salePrice && product.salePrice < product.price

  const toggleVisibility = async () => {
    const { response } = await productsApi.toggleVisibility(product._id)
    if (response?.success) onChanged()
  }

  const duplicate = async () => {
    const { response } = await productsApi.duplicate(product._id)
    if (response?.success) onChanged()
  }

  return (
    <div className='tw-rounded-[8px] tw-border tw-border-[#E7DFD9] tw-overflow-hidden tw-bg-white'>
      <div className='tw-relative tw-h-40 tw-bg-[#F2ECE7]'>
        {product.image && (
          <img
            src={product.image}
            alt={product.name}
            className='tw-h-full tw-w-full tw-object-cover'
          />
        )}
        <div className='tw-absolute tw-right-2 tw-top-2 tw-flex tw-gap-1'>
          {!product.isVisible && <StatusChip label='Hidden' tone='warning' />}
          {!product.isAvailable && (
            <StatusChip label='Unavailable' tone='danger' />
          )}
          {product.isFeatured && <StatusChip label='Featured' tone='success' />}
        </div>
      </div>
      <div className='tw-p-4'>
        <h3 className='tw-font-semibold tw-text-[15px]'>{product.name}</h3>
        <p className='tw-mt-1 tw-text-sm tw-text-gray-500 tw-line-clamp-2'>
          {product.shortDescription || product.description}
        </p>
        <div className='tw-mt-2 tw-flex tw-items-center tw-gap-2'>
          {onSale ? (
            <>
              <span className='tw-font-bold tw-text-navyBlue'>
                {currencyFormat(product.salePrice)}
              </span>
              <span className='tw-text-sm tw-text-gray-400 tw-line-through'>
                {currencyFormat(product.price)}
              </span>
            </>
          ) : (
            <span className='tw-font-bold'>{currencyFormat(product.price)}</span>
          )}
        </div>
        <div className='tw-mt-4 tw-flex tw-flex-wrap tw-gap-2'>
          <ActionButton label='Edit' onClick={() => onEdit(product)} />
          <ActionButton label='Duplicate' onClick={duplicate} />
          <ActionButton
            label={product.isVisible ? 'Hide' : 'Show'}
            onClick={toggleVisibility}
          />
          <ActionButton
            label='Delete'
            tone='danger'
            onClick={() => onDelete(product)}
          />
        </div>
      </div>
    </div>
  )
}

function ProductsInner() {
  const { loading, products, setSearch, refresh } = useProducts()
  const navigate = useNavigate()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (product) => {
    setEditing(product)
    setFormOpen(true)
  }

  const confirmDelete = async () => {
    const { response } = await productsApi.remove(deleting._id)
    if (response?.success) await refresh()
    setDeleting(null)
  }

  return (
    <div>
      <div className='tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-2'>
        <div>
          <button
            onClick={() => navigate('/order-management')}
            className='tw-text-[12px] tw-text-gray-400'
          >
            ← Order management
          </button>
          <h2 className='font-heading tw-text-2xl'>Products</h2>
        </div>
        <button
          onClick={openCreate}
          className='tw-rounded-[6px] tw-bg-electricGreen tw-text-white tw-text-regular tw-py-[8px] tw-px-[20px] tw-whitespace-nowrap'
        >
          + Add product
        </button>
      </div>

      <div className='tw-mb-6 tw-flex'>
        <SearchList search={(e) => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <Loader />
      ) : products.length ? (
        <div className='tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-3'>
          {products.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
              onEdit={openEdit}
              onChanged={refresh}
              onDelete={setDeleting}
            />
          ))}
        </div>
      ) : (
        <div className='tw-text-center tw-text-gray-400 tw-py-16'>
          No products found
        </div>
      )}

      <Dialoguemodal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'Edit product' : 'Create product'}
      >
        {formOpen && (
          <ProductForm
            product={editing}
            onClose={() => setFormOpen(false)}
          />
        )}
      </Dialoguemodal>

      <ConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      >
        <div className='tw-mb-4'>
          <h3 className='tw-text-lg tw-font-semibold tw-mb-1'>Delete product</h3>
          <p className='tw-text-sm tw-text-gray-500'>
            Are you sure you want to delete “{deleting?.name}”? This cannot be
            undone.
          </p>
        </div>
      </ConfirmationModal>
    </div>
  )
}

function Products() {
  return (
    <ProductsProvider>
      <ProductsInner />
    </ProductsProvider>
  )
}

export default Products
